import Component from '../../../shared/component';
import AvatarBuilderPreview from './avatar-builder-preview';
import AvatarBuilderOptions from './avatar-builder-options';
import AvatarBuilderSaveControl from './avatar-builder-save-control';
import AvatarBuilderMobileSaveControl from './avatar-builder-mobile-save-control';

// Constants
const MODAL_DISMISS_DELAY = 2000;
const AVATAR_CONFIGURATION_FIELD_CLASS = '.form-profile-avatarConfiguration';
const PROFILE_AVATAR_WRAPPER_CLASS = '.form-profile-avatar-wrapper';
const PROFILE_AVATAR_PREVIEW_WRAPPER_CLASS =
  '.form-profile-avatar-preview-wrapper';
const NAVBAR_AVATAR_IMAGE_CLASS = '.navbar-user-image';

export default class AvatarBuilder extends Component {
  constructor(element, opts) {
    super(...arguments);

    this.$element = $(element);

    this.$avatarBuilderError = opts.avatarBuilderError;

    this.userConfig = {};
    this.optionsConfig = {};

    this.previewMode = this.$element.data('preview-mode');

    this.avatarBuilderSaveControl = new AvatarBuilderSaveControl(
      '.menteeAvatarBuilderModal-footer',
      {avatarBuilder: this}
    );
    this.avatarBuilderMobileSaveControl = new AvatarBuilderMobileSaveControl(
      '.menteeAvatarBuilderModal-header-controls-links',
      {avatarBuilder: this}
    );
    this.avatarBuilderPreview = new AvatarBuilderPreview(
      '.menteeAvatarBuilder-preview'
    );
    this.avatarBuilderOptions = new AvatarBuilderOptions(
      '.menteeAvatarBuilderModal-body-options',
      {avatarBuilder: this}
    );
    this.$optionsZone = this.$element.find(
      '.menteeAvatarBuilderModal-body-options'
    );

    this.bindEvents();
  }

  bindEvents() {
    this.handleModalShown = this.handleModalShown.bind(this);
    this.handleModalShow = this.handleModalShow.bind(this);
    this.handleModalHidden = this.handleModalHidden.bind(this);

    this.$element.on('show.bs.modal.AvatarBuilder', this.handleModalShow);
    this.$element.on('shown.bs.modal.AvatarBuilder', this.handleModalShown);
    this.$element.on('hidden.bs.modal.AvatarBuilder', this.handleModalHidden);

    this.$optionsZone.scroll(() => {
      if (this.$optionsZone.scrollTop() <= 1) return;
      this.markOptionsAsScrolled();
    });
  }

  unbindEvents() {
    this.$element.off('show.bs.modal.AvatarBuilder', this.handleModalShow);
    this.$element.off('hidden.bs.modal.AvatarBuilder', this.handleModalShown);
    this.$element.off('hidden.bs.modal.AvatarBuilder', this.handleModalHidden);
    this.$optionsZone.off('scroll');

    this.destroyComponents();
  }

  handleModalShow() {
    this.avatarBuilderOptions.disableAllOptions();
    this.avatarBuilderSaveControl.displayLoading();
    this.avatarBuilderMobileSaveControl.displayLoading();
  }

  handleModalShown() {
    if (this.previewMode === 'preview' && this.avatarConfiguration)
      return this.restoreConfiguration();

    $.get(this.$element.data('get-config-url'))
      .done((data) => this.handleAvatarBuilderConfigLoaded(data))
      .fail(() => this.handleAvatarBuilderConfigError());
  }

  handleModalHidden() {
    this.$avatarBuilderError.hideError();
    this.avatarBuilderOptions.unselectAllOptions();
    this.avatarBuilderSaveControl.displayReady();
    this.avatarBuilderMobileSaveControl.displayReady();
    this.markOptionsAsUnscrolled();
  }

  handleAvatarBuilderConfigLoaded(data) {
    this.optionsConfig = data.optionsConfig;
    this.userConfig = data.userConfig;

    this.avatarBuilderPreview.setOptionsConfig(this.optionsConfig);
    this.baseConfiguration = this.buildBaseConfiguration(this.optionsConfig);
    this.avatarConfiguration = this.generateAvatarConfiguration(
      this.baseConfiguration,
      this.userConfig
    );

    this.restoreConfiguration();
  }

  handleAvatarBuilderConfigError() {
    this.$avatarBuilderError.displayError();
    this.avatarBuilderMobileSaveControl.hideLoading();
    this.avatarBuilderSaveControl.hideLoading();
  }

  destroyComponents() {
    this.avatarBuilderSaveControl.unbindEvents();
    this.avatarBuilderMobileSaveControl.unbindEvents();
    this.avatarBuilderPreview.unbindEvents();
    this.avatarBuilderOptions.unbindEvents();
  }

  buildBaseConfiguration(optionConfiguration) {
    return Object.keys(optionConfiguration).reduce((memo, category) => {
      memo[category] = this.buildBaseConfigurationNodeDefaultValues(
        optionConfiguration,
        category
      );
      return memo;
    }, {});
  }

  buildBaseConfigurationNodeDefaultValues(optionConfiguration, category) {
    const configurationValues = optionConfiguration[category];
    if (
      configurationValues[0] &&
      configurationValues[0].hasOwnProperty('asset')
    )
      return {option: null};

    return {color: null, option: null};
  }

  handleItemSelection(itemId, category, itemType, required) {
    const currentlySelectedItemId = this.findCurrentlySelectedItem(
      itemType,
      category
    );

    const categoryUpdated = this.updateCategorySelectedItem(
      currentlySelectedItemId,
      itemId,
      itemType,
      category,
      required
    );

    if (
      (this.isCategoryConfigurationComplete(category) && categoryUpdated) ||
      !required
    )
      this.updatePreview();
  }

  updateCategorySelectedItem(
    currentlySelectedItemId,
    itemId,
    itemType,
    category,
    required
  ) {
    if (currentlySelectedItemId) {
      if (currentlySelectedItemId === itemId && required) return false;

      this.markItemAsUnselected(currentlySelectedItemId, itemType, category);
    }

    if (currentlySelectedItemId !== itemId)
      this.markItemAsSelected(itemId, itemType, category);

    return true;
  }

  handleCategoryOptionReset(category) {
    const itemType = 'option';
    const currentlySelectedItemId = this.findCurrentlySelectedItem(
      itemType,
      category
    );

    if (currentlySelectedItemId) {
      this.markItemAsUnselected(currentlySelectedItemId, itemType, category);
    }
    this.updatePreview();
  }

  isCategoryConfigurationComplete(category) {
    const values = Object.values(this.avatarConfiguration[category]);

    return !values.includes(null);
  }

  findCurrentlySelectedItem(itemType, category) {
    return this.avatarConfiguration[category][itemType];
  }

  markItemAsSelected(itemId, itemType, category) {
    this.avatarBuilderOptions.selectItem(itemId, itemType, category);
    this.avatarConfiguration[category][itemType] = itemId;
  }

  markItemAsUnselected(itemId, itemType, category) {
    this.avatarConfiguration[category][itemType] = null;
    this.avatarBuilderOptions.unselectItem(itemId, itemType, category);
  }

  generateAvatarConfiguration(baseConfiguration, userConfiguration) {
    return Object.assign(baseConfiguration, userConfiguration);
  }

  updatePreview() {
    this.avatarBuilderPreview.updatePreview(this.avatarConfiguration);
  }

  updateConfiguration() {
    if (this.previewMode === 'preview')
      return this.updateConfigurationPreview();

    this.updateConfigurationLive();
  }

  updateConfigurationPreview() {
    $(AVATAR_CONFIGURATION_FIELD_CLASS).val(
      JSON.stringify(this.avatarConfiguration)
    );

    $(PROFILE_AVATAR_WRAPPER_CLASS).hide();
    $(PROFILE_AVATAR_PREVIEW_WRAPPER_CLASS).show();

    this.$element.modal('hide');
  }

  updateConfigurationLive() {
    const payload = JSON.stringify({
      // eslint-disable-next-line camelcase
      mentee_profile: {avatar_configuration: this.avatarConfiguration},
    });

    $.ajax({
      url: this.$element.data('update-config-url'),
      data: payload,
      dataType: 'json',
      contentType: 'application/json',
      method: 'PUT',
    })
      .done((data) => {
        this.avatarBuilderSaveControl.handleSaved();
        this.avatarBuilderMobileSaveControl.handleSaved();
        this.updatePageAvatars(data.avatarUrl);
        this.dismissBuilder();
      })
      .fail(() => window.console.error('updateConfiguration ERROR'));
  }

  dismissBuilder() {
    setTimeout(() => {
      this.$element.modal('hide');
    }, MODAL_DISMISS_DELAY);
  }

  restoreConfiguration() {
    Object.entries(this.avatarConfiguration).forEach(
      ([category, categoryConfig]) => {
        if (!categoryConfig.option) return;

        this.avatarBuilderOptions.selectItem(
          categoryConfig.option,
          'option',
          category
        );
        if (categoryConfig.color)
          this.avatarBuilderOptions.selectItem(
            categoryConfig.color,
            'color',
            category
          );
      }
    );

    this.updatePreview();
    this.avatarBuilderSaveControl.displayReady();
    this.avatarBuilderMobileSaveControl.displayReady();
    this.avatarBuilderOptions.enableAllOptions();
  }

  updatePageAvatars(avatarUrl) {
    $(NAVBAR_AVATAR_IMAGE_CLASS).attr('src', avatarUrl);
  }

  markOptionsAsScrolled() {
    this.avatarBuilderPreview.$element.addClass('minimized');
    this.$optionsZone.addClass('scrolled');
  }

  markOptionsAsUnscrolled() {
    this.avatarBuilderPreview.$element.removeClass('minimized');
    this.$optionsZone.removeClass('scrolled');
  }
}
